// src/pages/OrderSettingsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import LoadingSpinner from '../components/loadingspinner';
import Switch from '../components/Switch';
import './OrderSettingsPage.css';

function OrderSettingsPage() {
  const { restaurantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restaurant, setRestaurant] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([
    { id: 'new', name: 'New Orders', isDefault: true, canDelete: false },
    { id: 'completed', name: 'Order History', isDefault: true, canDelete: false },
    { id: 'cancelled', name: 'Cancelled Orders', isDefault: true, canDelete: false }
  ]);
  const [newStepName, setNewStepName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        setLoading(true);
        const restaurantRef = doc(db, 'restaurants', restaurantId);
        const restaurantSnap = await getDoc(restaurantRef);

        if (restaurantSnap.exists()) {
          const restaurantData = { id: restaurantSnap.id, ...restaurantSnap.data() };
          setRestaurant(restaurantData);
          
          // Load existing workflow steps if they exist
          if (restaurantData.orderWorkflow && restaurantData.orderWorkflow.length > 0) {
            setWorkflowSteps(restaurantData.orderWorkflow);
          }
        } else {
          setError('Restaurant not found.');
        }
      } catch (err) {
        console.error('Error fetching restaurant data:', err);
        setError('Failed to load restaurant data.');
      } finally {
        setLoading(false);
      }
    };

    if (restaurantId) {
      fetchRestaurantData();
    }
  }, [restaurantId]);

  const handleAddStep = () => {
    if (!newStepName.trim()) return;
    
    const newStep = {
      id: `custom_${Date.now()}`,
      name: newStepName.trim(),
      isDefault: false,
      canDelete: true
    };
    
    // Insert the new step before the last two default steps (completed and cancelled)
    const updatedSteps = [...workflowSteps];
    updatedSteps.splice(-2, 0, newStep);
    setWorkflowSteps(updatedSteps);
    setNewStepName('');
  };

  const handleRemoveStep = (stepId) => {
    setWorkflowSteps(workflowSteps.filter(step => step.id !== stepId));
  };

  const handleMoveStep = (stepId, direction) => {
    const currentIndex = workflowSteps.findIndex(step => step.id === stepId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Prevent moving default steps or moving beyond boundaries
    if (newIndex < 1 || newIndex >= workflowSteps.length - 2) return;
    if (workflowSteps[newIndex].isDefault && (newIndex === 0 || newIndex >= workflowSteps.length - 2)) return;

    const updatedSteps = [...workflowSteps];
    [updatedSteps[currentIndex], updatedSteps[newIndex]] = [updatedSteps[newIndex], updatedSteps[currentIndex]];
    setWorkflowSteps(updatedSteps);
  };

  const handleSaveWorkflow = async () => {
    try {
      setIsSaving(true);
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      await updateDoc(restaurantRef, {
        orderWorkflow: workflowSteps
      });
      
      setError('');
      alert('Order workflow saved successfully!');
    } catch (err) {
      console.error('Error saving workflow:', err);
      setError('Failed to save workflow. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (window.confirm('Are you sure you want to reset to the default workflow? This will remove all custom steps.')) {
      setWorkflowSteps([
        { id: 'new', name: 'New Orders', isDefault: true, canDelete: false },
        { id: 'completed', name: 'Order History', isDefault: true, canDelete: false },
        { id: 'cancelled', name: 'Cancelled Orders', isDefault: true, canDelete: false }
      ]);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && !restaurant) {
    return <div className="container centered"><p className="error-message">{error}</p></div>;
  }

  return (
    <div className="container order-settings-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Order Workflow Settings</h1>
        <Link to={`/dashboard/${restaurantId}`} className="btn" style={{width:"fit-content"}}>Back to Dashboard</Link>
      </div>

      {error && <div className="error-message" style={{marginBottom: '20px'}}>{error}</div>}

      <div className="workflow-section">
        <h2>Current Order Workflow</h2>
        <p className="workflow-description">
          Configure the stages that orders go through in your restaurant. Orders will move from one stage to the next based on your actions.
        </p>

        <div className="workflow-steps">
          {workflowSteps.map((step, index) => (
            <div key={step.id} className={`workflow-step ${step.isDefault ? 'default-step' : 'custom-step'}`}>
              <div className="step-info">
                <span className="step-number">{index + 1}</span>
                <span className="step-name">{step.name}</span>
                {step.isDefault && <span className="default-badge">Default</span>}
              </div>
              
              <div className="step-actions">
                {!step.isDefault && index > 1 && index < workflowSteps.length - 2 && (
                  <>
                    <button 
                      onClick={() => handleMoveStep(step.id, 'up')} 
                      className="btn btn-sm btn-outline"
                      disabled={index <= 1}
                    >
                      ↑
                    </button>
                    <button 
                      onClick={() => handleMoveStep(step.id, 'down')} 
                      className="btn btn-sm btn-outline"
                      disabled={index >= workflowSteps.length - 3}
                    >
                      ↓
                    </button>
                  </>
                )}
                
                {step.canDelete && (
                  <button 
                    onClick={() => handleRemoveStep(step.id)} 
                    className="btn btn-sm btn-danger"
                  >
                    Remove
                  </button>
                )}
              </div>
              
              {index < workflowSteps.length - 1 && (
                <div className="step-arrow">→</div>
              )}
            </div>
          ))}
        </div>

        <div className="add-step-section">
          <h3>Add Custom Step</h3>
          <div className="add-step-form">
            <input
              type="text"
              placeholder="Enter step name (e.g., Kitchen, Billing, Ready for Pickup)"
              value={newStepName}
              onChange={(e) => setNewStepName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddStep()}
            />
            <button onClick={handleAddStep} className="btn btn-primary" disabled={!newStepName.trim()}>
              Add Step
            </button>
          </div>
          <p className="add-step-note">
            Custom steps will be added between "New Orders" and "Order History/Cancelled Orders"
          </p>
        </div>

        <div className="workflow-actions">
          <button onClick={handleSaveWorkflow} className="btn btn-success" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Workflow'}
          </button>
          <button onClick={handleResetToDefault} className="btn btn-outline">
            Reset to Default
          </button>
        </div>

        <div className="workflow-info">
          <h3>How it works:</h3>
          <ul>
            <li>Orders start in "New Orders" when customers place them</li>
            <li>You can move orders through each custom step you've created</li>
            <li>Orders can be moved to "Cancelled Orders" from any step</li>
            <li>Completed orders end up in "Order History"</li>
            <li>The order of steps determines the workflow sequence</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default OrderSettingsPage;